// ContactRenderer.tsx
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Linkedin, Loader2, Phone, Sparkles } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { BaseEntityProps, ContactDTO, EntityRowAction } from '#types/types';
import { portalTableRow } from '../portal/portalTheme';
import { EntitySkeleton } from './EntityView';
import { EntityRowActions } from './EntityRowActions';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';

interface ContactRendererProps extends BaseEntityProps<ContactDTO> {
  columns?: TableColumnDef[];
  rowActions?: EntityRowAction<ContactDTO>[];
}

const EMPTY_ROW_ACTIONS: EntityRowAction<ContactDTO>[] = [];

const PROGRESS_GRADIENT = 'linear-gradient(90deg, #0C2A82, #1034A6, #1E50C8, #3B6FE0, #8AAAEE)';

function stopPropagation(e: React.MouseEvent) {
  e.stopPropagation();
}

export const ContactRenderer = memo(({
  entityId,
  variant = 'row',
  className,
  columns,
  rowActions = EMPTY_ROW_ACTIONS,
}: ContactRendererProps) => {
  const data = useKernelStore((state) => state.inventory.Contact.registry[entityId]);
  const { kernel } = useKernel();
  const lang = useKernelStore((state) => state.language);
  const [isEnriching, setIsEnriching] = useState(false);
  const handleEnrich = useCallback(async () => {
    const k = kernel.current;
    if (!k || !data) return;
    setIsEnriching(true);
    try {
      await k.enrichContact(data.id, data.parentType === 'brand' ? data.parentId : undefined);
    } catch (e) {
      k.handleError(e instanceof Error ? e.message : 'Contact enrichment failed');
    } finally {
      setIsEnriching(false);
    }
  }, [kernel, data]);

  const handleEnrichClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    void handleEnrich();
  }, [handleEnrich]);

  // Progressive blue-rainbow bar while the contact enriches — same loading language
  // as the other enrichments (discovery / bulk). Ramps toward ~92% over the FullEnrich
  // waterfall (30s–3min), then snaps to 100% when it lands.
  const [enrichProgress, setEnrichProgress] = useState(0);
  useEffect(() => {
    if (isEnriching) {
      setEnrichProgress((p) => (p < 8 ? 8 : p));
      const iid = window.setInterval(() => {
        setEnrichProgress((p) => (p >= 92 ? p : Math.min(92, p + Math.max(0.3, (92 - p) * 0.03))));
      }, 450);
      return () => window.clearInterval(iid);
    }
    setEnrichProgress((p) => (p > 0 ? 100 : 0));
    const tid = window.setTimeout(() => setEnrichProgress(0), 600);
    return () => window.clearTimeout(tid);
  }, [isEnriching]);

  const progressBarStyle = useMemo<React.CSSProperties>(() => ({
    width: `${enrichProgress}%`,
    background: PROGRESS_GRADIENT,
  }), [enrichProgress]);

  const cellStyleByColumnId = useMemo(() => {
    const map: Record<string, React.CSSProperties> = {};
    (columns || []).forEach((column) => {
      map[column.id] = {
        justifyContent:
          column.id === 'actions' || column.align === 'right'
            ? 'flex-end'
            : column.align === 'center'
              ? 'center'
              : 'flex-start',
      };
    });
    return map;
  }, [columns]);

  if (!data) return <EntitySkeleton variant={variant} className={className} />;

  const name = data.fullName || [data.firstName, data.lastName].filter(Boolean).join(' ') || 'Unknown';
  const phone = data.directPhone || data.primaryPhone || '';
  const location = [data.city, data.state, data.country].filter(Boolean).join(', ');
  const department = Array.isArray(data.departments) ? data.departments.filter(Boolean).join(', ') : '';

  if (variant === 'row') {
    const enrichBtn = (
      <button
        type="button"
        onClick={handleEnrichClick}
        disabled={isEnriching}
        title={lang === 'fr' ? 'Enrichir' : 'Enrich'}
        aria-label={lang === 'fr' ? 'Enrichir' : 'Enrich'}
        className="inline-flex shrink-0 items-center justify-center rounded p-1 text-[#6E6E6E] outline-none transition-colors hover:text-[#E8E8E8] focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isEnriching ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
      </button>
    );
    const cellRenderers: Record<string, React.ReactNode> = {
      avatar: (
        <div className="flex justify-center">
          <div className="w-6 h-6 rounded-full bg-white/10 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
            {data.photoUrl ? (
              <img src={data.photoUrl} alt={name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-[9px] text-white/60 font-normal">{name.slice(0, 1).toUpperCase()}</span>
            )}
          </div>
        </div>
      ),
      name: (
        <div className="px-2 truncate text-white font-normal flex items-center gap-2" title={name}>
          <span className="truncate">{name}</span>
          {data.linkedinUrl && (
            <a href={data.linkedinUrl} target="_blank" rel="noopener noreferrer" className="shrink-0 text-white/35 hover:text-white/70" onClick={stopPropagation}>
              <Linkedin size={12} />
            </a>
          )}
        </div>
      ),
      firstName: (
        <div className="px-2 truncate text-white/80" title={data.firstName ?? ''}>
          {data.firstName || <span className="text-white/20 italic">n/a</span>}
        </div>
      ),
      lastName: (
        <div className="px-2 truncate text-white/80" title={data.lastName ?? ''}>
          {data.lastName || <span className="text-white/20 italic">n/a</span>}
        </div>
      ),
      role: (
        <div className="px-2 truncate text-white/55" title={data.title ?? ''}>
          {data.title || data.seniority || 'Unknown role'}
        </div>
      ),
      company: (
        <div className="px-2 truncate text-white/80" title={data.organizationName ?? ''}>
          {data.organizationName || <span className="text-white/20 italic">n/a</span>}
        </div>
      ),
      department: (
        <div className="px-2 truncate text-white/55" title={department}>
          {department || <span className="text-white/20 italic">n/a</span>}
        </div>
      ),
      email: (
        <div className="flex min-w-0 items-center gap-1.5 px-2 text-white/55" title={data.email ?? ''}>
          {data.email ? <span className="truncate">{data.email}</span> : enrichBtn}
        </div>
      ),
      phone: (
        <div className="px-2 truncate">
          {phone ? (
            <a
              href={`tel:${phone.replace(/[^+\d]/g, '')}`}
              onClick={stopPropagation}
              title={`Call ${phone}`}
              className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-[#2A2A2A] bg-[#1F1F1F] px-2 py-1 text-[11px] text-[#B5B5B5] transition-colors hover:border-[#3A6FF8] hover:text-white"
            >
              <Phone size={11} className="shrink-0" />
              <span className="truncate">{phone}</span>
            </a>
          ) : (
            enrichBtn
          )}
        </div>
      ),
      location: (
        <div className="px-2 truncate text-white/55" title={location}>
          {location || <span className="text-white/20 italic">n/a</span>}
        </div>
      ),
      actions: (
        <div className="flex justify-center">
          <EntityRowActions entityType="Contact" entity={data} rowActions={rowActions} />
          {enrichBtn}
        </div>
      ),
    };

    const activeColumns = columns || [];

    return (
      <div
        className={clsx(
          portalTableRow,
          styles.gridRow,
          styles.bodyRow,
          'group relative h-10 border-b border-[#2A2A2A]/50 text-[13px] transition-colors bg-transparent hover:bg-white/[0.03]',
          className
        )}
      >
        {(isEnriching || enrichProgress > 0) && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[2px] overflow-hidden bg-[#242424]">
            <div
              className="h-full transition-[width] duration-300 ease-out"
              style={progressBarStyle}
            />
          </div>
        )}
        {activeColumns.map((column) => (
          <div
            key={column.id}
            className={clsx(styles.cell, column.id === 'actions' && styles.actionCell)}
            style={cellStyleByColumnId[column.id]}
          >
            {cellRenderers[column.id] || null}
          </div>
        ))}
      </div>
    );
  }
  return null;
});

ContactRenderer.displayName = 'ContactRenderer';
